/** Serverzustand des Kundenportals. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createMyTicket,
  fetchPortalSettings,
  getMyTicket,
  getPortalArticle,
  listMyTicketArticles,
  listMyTickets,
  replyToMyTicket,
  listPortalForms,
  searchPortalKnowledge,
  suggestPortalArticles,
  submitPortalForm,
} from './api';

export const portalKeys = {
  all: ['portal'] as const,
  settings: ['portal', 'settings'] as const,
  tickets: ['portal', 'tickets'] as const,
  ticket: (ticketId: string) => ['portal', 'ticket', ticketId] as const,
  articles: (ticketId: string) => ['portal', 'articles', ticketId] as const,
  forms: ['portal', 'forms'] as const,
  knowledge: (query: string) => ['portal', 'knowledge', query] as const,
  article: (articleId: string) => ['portal', 'article', articleId] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function usePortalSettings() {
  return useQuery({
    queryKey: portalKeys.settings,
    queryFn: fetchPortalSettings,
    // Das Erscheinungsbild aendert sich selten -- und wenn, dann reicht es,
    // wenn der naechste Besuch es sieht.
    staleTime: 10 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useMyTickets() {
  return useQuery({
    queryKey: portalKeys.tickets,
    queryFn: listMyTickets,
    retry: retryUnlessPermanent,
  });
}

export function useMyTicket(ticketId: string | null) {
  const id = ticketId ?? '';
  return useQuery({
    queryKey: portalKeys.ticket(id),
    queryFn: () => getMyTicket(id),
    enabled: ticketId !== null,
    retry: retryUnlessPermanent,
  });
}

export function useMyTicketArticles(ticketId: string | null) {
  const id = ticketId ?? '';
  return useQuery({
    queryKey: portalKeys.articles(id),
    queryFn: () => listMyTicketArticles(id),
    enabled: ticketId !== null,
    retry: retryUnlessPermanent,
  });
}

export function usePortalKnowledge(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: portalKeys.knowledge(trimmed),
    queryFn: () => searchPortalKnowledge(trimmed),
    enabled: trimmed.length >= 3,
    retry: retryUnlessPermanent,
  });
}

/** Loesungsvorschlaege waehrend der Erfassung -- F4-13, K-107. */
export function usePortalSuggestions(text: string) {
  const trimmed = text.trim();
  return useQuery({
    queryKey: ['portal', 'suggestions', trimmed],
    queryFn: () => suggestPortalArticles(trimmed),
    enabled: trimmed.length >= 3,
    // Eine halbe Minute: Der Betreff aendert sich bei jedem Zeichen, der
    // Artikelbestand waehrend des Tippens nicht.
    staleTime: 30_000,
    retry: retryUnlessPermanent,
  });
}

export function usePortalArticle(articleId: string | null) {
  const id = articleId ?? '';
  return useQuery({
    queryKey: portalKeys.article(id),
    queryFn: () => getPortalArticle(id),
    enabled: articleId !== null,
    retry: retryUnlessPermanent,
  });
}

export function useCreateMyTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: { title: string; description: string }) =>
      createMyTicket(values.title, values.description),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portalKeys.tickets });
    },
  });
}

export function useReplyToMyTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bodyHtml: string) => replyToMyTicket(ticketId, bodyHtml),
    onSuccess: async () => {
      // Der Verlauf **und** das Ticket: Eine Rueckmeldung setzt den Vorgang
      // in aller Regel zurueck auf "in Bearbeitung", und der Zustand steht
      // in der Kopfzeile.
      await queryClient.invalidateQueries({ queryKey: portalKeys.all });
    },
  });
}

/**
 * Die Anfragetypen des Portals -- F4-20.
 *
 * Zehn Minuten frisch: Ein Anfragetyp kommt selten dazu, und wer gerade ein
 * Formular ausfuellt, soll es nicht unter den Haenden wechseln sehen.
 */
export function usePortalForms() {
  return useQuery({
    queryKey: portalKeys.forms,
    queryFn: listPortalForms,
    staleTime: 10 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useSubmitPortalForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: {
      formId: string;
      subject: string;
      message: string;
      answers: Record<string, unknown>;
    }) =>
      submitPortalForm(values.formId, {
        subject: values.subject,
        message: values.message,
        answers: values.answers,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portalKeys.tickets });
    },
  });
}
